import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useRef, useMemo, useState, useEffect, useLayoutEffect } from 'react';
import { IconButton } from '@paypalcorp/pp-react-icon-button';
import { OverflowIcon } from '@paypalcorp/pp-react-icons';
import { devWarning, utilStyles, ZINDEX_DROPDOWN, getPx, KeyboardCode } from '@paypalcorp/pp-react-utils';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5;
var menuHeightMax = '16rem';
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceSm = tokens.sysSpaceSm,
      sysColorBackgroundSurface = tokens.sysColorBackgroundSurface,
      sysColorBackgroundSurfaceHover = tokens.sysColorBackgroundSurfaceHover,
      sysBorderRadiusLg = tokens.sysBorderRadiusLg,
      sysColorBackgroundSurfaceActive = tokens.sysColorBackgroundSurfaceActive;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles,
      elevationStyles = _utilStyles.elevationStyles;

  var _textStyles = textStyles(tokens),
      textBody = _textStyles.body;

  var overflowMenuBase = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: overflow_menu_base;\n    position: relative;\n    display: inline-block;\n    max-height: ", ";\n  "])), menuHeightMax);
  var keyboardActive = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: keyboard_active;\n    &:focus {\n      ", ";\n    }\n  "])), focusStyles.focusInlineElement);
  var btnActive = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: btn_active;\n    &[aria-expanded='true'] {\n      background: ", ";\n    }\n  "])), sysColorBackgroundSurfaceActive);
  var list = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: list;\n    right: 0;\n    margin: 0;\n    position: fixed;\n    z-index: ", ";\n    list-style: none;\n    border-radius: ", ";\n    ", "\n    background-color: ", ";\n    display: none;\n    padding: ", " 0;\n    overflow: auto;\n    width: fit-content;\n\n    &[aria-hidden='false'] {\n      display: inline-block;\n    }\n    [dir='rtl'] & {\n      right: auto; // remove ltr style\n      left: ", ";\n    }\n  "])), ZINDEX_DROPDOWN, sysBorderRadiusLg, elevationStyles.level1, sysColorBackgroundSurface, sysSpaceSm, sysSpaceMd);
  var listItem = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: list_item;\n    white-space: nowrap;\n    display: block;\n    text-align: justify;\n    text-overflow: ellipsis;\n    position: relative;\n    ", "\n    text-decoration:none;\n    padding: ", " ", ";\n    &:hover {\n      background-color: ", ";\n      cursor: pointer;\n      outline: 0;\n    }\n\n    &:focus {\n      // override link styles\n      box-shadow: none;\n      outline: 0;\n      border-radius: 0;\n    }\n  "])), textBody(), sysSpaceMd, sysSpaceLg, sysColorBackgroundSurfaceHover);
  return {
    overflowMenuBase: overflowMenuBase,
    keyboardActive: keyboardActive,
    btnActive: btnActive,
    list: list,
    listItem: listItem
  };
}

var _excluded = ["name", "value", "ariaLabel", "options", "isOpen", "onChange", "onOpen", "onClose", "className", "triggerClassName", "theme"],
    _excluded2 = ["label", "value", "href"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var OverflowMenu = function (_ref) {
  var _ref$name = _ref.name,
      name = _ref$name === void 0 ? 'Overflow' : _ref$name,
      _ref$value = _ref.value,
      value = _ref$value === void 0 ? 0 : _ref$value,
      _ref$ariaLabel = _ref.ariaLabel,
      ariaLabel = _ref$ariaLabel === void 0 ? name : _ref$ariaLabel,
      _ref$options = _ref.options,
      options = _ref$options === void 0 ? [] : _ref$options,
      isOpen = _ref.isOpen,
      onChange = _ref.onChange,
      onOpen = _ref.onOpen,
      onClose = _ref.onClose,
      className = _ref.className,
      triggerClassName = _ref.triggerClassName,
      theme = _ref.theme,
      rest = _objectWithoutProperties(_ref, _excluded);

  var wrapperRef = useRef();
  var menuRef = useRef();
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focusedIndex = _useState2[0],
      setFocusedIndex = _useState2[1];

  var listItemCss = [_styles.listItem, focusedIndex && _styles.keyboardActive];
  var optionList = options.map(function (_ref2, index) {
    var label = _ref2.label,
        optValue = _ref2.value,
        _ref2$href = _ref2.href,
        href = _ref2$href === void 0 ? '#' : _ref2$href,
        otherProps = _objectWithoutProperties(_ref2, _excluded2);

    return jsx("li", {
      role: "presentation",
      key: "ofmenu_".concat(index),
      "data-ppui": "true"
    }, jsx("a", _extends({
      href: href,
      "data-name": name,
      "data-value": optValue,
      css: listItemCss,
      role: "menuitem",
      tabIndex: -1
    }, otherProps, {
      "data-ppui": "true"
    }), label));
  });

  var getWrapperCurrent = function (w) {
    return (
      /* istanbul ignore next */
      w && w.current ? w.current : null
    );
  };
  /**
   * Determines whether the click is outside the element and invoke `onClose` function.
   */


  function handleClickOutside(evt) {
    var current = getWrapperCurrent(wrapperRef);

    if (isOpen && current && !current.contains(evt.target)) {
      evt.stopPropagation();
      onClose(evt);
    }
  }

  var getActiveIndex = function (menuItems) {
    for (var i = 0; i < menuItems.length; ++i) {
      if (menuItems[i] === document.activeElement) {
        return i;
      }
    }

    return 0;
  };

  function setFocus(offset) {
    var current = getWrapperCurrent(wrapperRef);
    /* istanbul ignore next */

    if (current) {
      var menuItems = current.querySelectorAll('[role="menuitem"]');
      var len = menuItems.length;
      var curIndex = getActiveIndex(menuItems);
      menuItems[(curIndex + offset + len) % len].focus();
    }
  }

  function handleKey(evt) {
    /* istanbul ignore next */
    var KeyCode = new KeyboardCode(evt.key || evt.keyCode);

    if (KeyCode.IS_ARROW_UP) {
      setFocus(-1);
      setFocusedIndex(true);
    } else if (KeyCode.IS_ARROW_DOWN) {
      setFocus(1);
      setFocusedIndex(true);
    } else if (KeyCode.IS_ESC || KeyCode.IS_TAB) {
      onClose(evt);
    } else {
      return; // Don't stopPropagation() if not one of the above
    }

    evt.preventDefault();
    evt.stopPropagation();
  }

  var getComputedDirection = function (elem) {
    return getComputedStyle(elem).direction;
  };

  useEffect(function () {
    // Now that we're using position: fixed for the menu,
    // We need to close whenever the content scrolls to avoid
    // having the trigger button scroll away from its menu
    // We could "stalk" the trigger and follow it around
    // But if we did that, the button could get clipped leaving a
    // disembodied menu...  --BCC
    // istanbul ignore next
    function handleScroll(evt) {
      var target = evt.target;

      if (target !== (menuRef === null || menuRef === void 0 ? void 0 : menuRef.current)) {
        onClose(evt);
      }
    } // istanbul ignore next


    function handleResize(evt) {
      onClose(evt);
    }

    function startListening() {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKey);
      window.addEventListener('scroll', handleScroll, {
        passive: true,
        capture: true
      });
      window.addEventListener('resize', handleResize);
    }

    function stopListening() {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    }

    if (isOpen) {
      startListening();
      setFocus(value);
    } else {
      stopListening();
    }

    return function () {
      stopListening();
    };
  }, [isOpen]);
  /*
   * The following code is very tricky. There are things you have to keep
   * straight in your mind:
   * - some values are strings and others are numbers
   * - some values are in px others in rem. numbers always represent px
   * - sometimes "right" is an x coordinate, others it's an inset FROM the right
   * - sometimes "bottom" is a y coordinate, others it's an inset FROM the bottom
   * - the getPx() utility will parse '0.75rem' or '12px' to the same value: 12
   *
   * --love BCC
   */
  // istanbul ignore next

  var stayInsideViewport = function (menu, wrapper, viewPort, isRtl) {
    var visBottom = viewPort.offsetTop + viewPort.height;
    var sysSpaceSm = tokens.sysSpaceSm;
    var smSpace = getPx(sysSpaceSm); // keep the top/bottom from touching the edge
    // The default case is to put the menu below the trigger and line up their right edges

    var targetBounds = wrapper.getBoundingClientRect(); // menu.style.top = `${targetBounds.bottom - offsetY + smSpace}px`
    // Now that it's in place, ask the browser about its dimensions

    var menuBounds = menu.getBoundingClientRect(); // Does the menu go off the bottom of the visual viewport?

    if (menuBounds.bottom > visBottom) {
      // consider putting the menu above the trigger instead
      var proposedTop = targetBounds.top - (menuBounds.height + smSpace); // but... would that send menu off the top of the screen?

      if (proposedTop < 0) {
        // The menu won't fit above or below - we must shorten the menu
        // So which is the lesser evil? Above or Below?
        if (Math.abs(proposedTop) < menuBounds.bottom - visBottom) {
          // Above: slide it down so the top is visible with a little breathing room
          menu.style.top = "".concat(smSpace, "px"); // wants a string here!
        } else {
          menu.style.top = '';
          menu.style.bottom = sysSpaceSm; // wants a string here!
        }
      } else {
        // It fits above - go with that
        // menu.style.left = `${wrapperRect.right - menuRect.width - offset.x}px`
        menu.style.top = "".concat(proposedTop - viewPort.offsetTop, "px");
      }
    } // We don't check for a fit here. If we need to we can set a max-width of 1vw - margins


    if (isRtl && menuBounds.right > viewPort.offsetLeft + viewPort.width) {
      menu.style.left = '';
      menu.style.right = "".concat(viewPort.width - targetBounds.right, "px");
    } else if (menuBounds.left < viewPort.offsetLeft) {
      menu.style.right = '';
      menu.style.left = "".concat(targetBounds.left - viewPort.offsetLeft, "px");
    }
  }; // Make a bounding client rect look like a visual viewport


  var visualViewportFrom = function (_ref4) {
    var height = _ref4.height,
        offsetLeft = _ref4.left,
        offsetTop = _ref4.top,
        width = _ref4.width;
    return {
      offsetLeft: offsetLeft,
      offsetTop: offsetTop,
      height: height,
      width: width
    };
  }; // istanbul ignore next


  var offsetBounds = function (wrapper) {
    var _wrapper$offsetParent, _window$visualViewpor, _window;

    var op = (_wrapper$offsetParent = wrapper.offsetParent) !== null && _wrapper$offsetParent !== void 0 ? _wrapper$offsetParent : document.body;
    var offsetTransform = window.getComputedStyle(op).transform;

    if ((offsetTransform !== null && offsetTransform !== void 0 ? offsetTransform : 'none') !== 'none') {
      return visualViewportFrom(op.getBoundingClientRect());
    }

    return (_window$visualViewpor = (_window = window) === null || _window === void 0 ? void 0 : _window.visualViewport) !== null && _window$visualViewpor !== void 0 ? _window$visualViewpor : visualViewportFrom(document.body.getBoundingClientRect());
  };

  useLayoutEffect(function () {
    var menu = menuRef.current;
    var wrapper = wrapperRef.current;
    var sysSpaceXs = tokens.sysSpaceXs;
    var spaceXs = getPx(sysSpaceXs);
    var wrapperRect = wrapper.getBoundingClientRect();
    var menuRect = menu.getBoundingClientRect();
    var isRtl = getComputedDirection(wrapper) === 'rtl';
    var localViewport = offsetBounds(wrapper);
    menu.style.top = "".concat(wrapperRect.bottom - localViewport.offsetTop + spaceXs, "px"); // clear out any old styles so we can start over

    menu.style.bottom = menu.style.left = menu.style.right = menu.style.maxHeight = ''; // istanbul ignore next

    if (isRtl) {
      menu.style.left = "".concat(wrapperRect.left + spaceXs, "px");
    } else {
      // need to position from left side since we don't know how far away the
      // right side might be...
      menu.style.left = "".concat(wrapperRect.right - menuRect.width - localViewport.offsetLeft, "px");
    }

    stayInsideViewport(menu, wrapper, localViewport, isRtl);
  }, [isOpen]);
  var triggerCss = [isOpen && _styles.btnActive]; // I switched from <ul> to <menu> Spec says they're interchangeable and
  // <menu> is semantically more readable. If nothing else makes it easier to debug

  return jsx("div", _extends({
    css: _styles.overflowMenuBase,
    className: className,
    ref: wrapperRef,
    "data-ppui-info": "overflow-menu_3.6.6"
  }, rest), jsx(IconButton, {
    className: triggerClassName,
    buttonType: "tertiary",
    css: triggerCss,
    name: name,
    svgIconComponent: OverflowIcon,
    "aria-haspopup": "true",
    "aria-expanded": isOpen,
    onClick: function handleClick(evt) {
      if (isOpen) {
        onClose(evt);
      } else {
        onOpen(evt);
        /* istanbul ignore next */

        setFocusedIndex(evt.detail === 0); // 1 = click, 0 = keyboard
      }
    },
    "data-ppui": "true"
  }, ariaLabel), jsx("menu", {
    ref: menuRef,
    css: _styles.list,
    tabIndex: -1,
    role: "menu",
    onClick: function itemClickHandler(evt) {
      var target = evt.target; // @ts-ignore

      var targetName = target.dataset.name; // @ts-ignore

      var targetValue = target.dataset.value;

      var namedTarget = _objectSpread(_objectSpread({}, target), {}, {
        name: targetName,
        value: targetValue
      });

      if (onChange) {
        evt.preventDefault();
        evt.stopPropagation(); // @ts-ignore

        onChange(_objectSpread(_objectSpread({}, evt), {}, {
          target: namedTarget
        }));
      }

      onClose(evt);
    },
    "aria-label": name,
    "aria-hidden": !isOpen,
    "data-ppui": "true"
  }, optionList));
};

export { OverflowMenu, styles as overflowMenuStyles };
